import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Database, 
  Download, 
  Trash2, 
  RefreshCw, 
  Eye,
  FileText,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DatabaseTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionData, setCollectionData] = useState([]);
  const [showDataModal, setShowDataModal] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/database/stats`);
      setStats(response.data);
      setCollections(response.data.collections || []);
    } catch (error) {
      toast.error('İstatistikler alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const viewCollection = async (collectionName) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/database/collection/${collectionName}`);
      setCollectionData(response.data);
      setSelectedCollection(collectionName);
      setShowDataModal(true);
    } catch (error) {
      toast.error('Veri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const exportDatabase = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/database/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `avrasya_sportif_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Veritabanı dışa aktarıldı');
    } catch (error) {
      toast.error('Dışa aktarma başarısız');
    } finally {
      setLoading(false);
    }
  };

  const clearCollection = async (collectionName) => {
    if (!window.confirm(`${collectionName} collection'ındaki TÜM verileri silmek istediğinize emin misiniz?`)) return;
    
    try {
      setLoading(true);
      await axios.delete(`${API}/database/collection/${collectionName}/clear`);
      toast.success(`${collectionName} temizlendi`);
      fetchStats();
    } catch (error) {
      toast.error('Temizleme başarısız');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-800">Veritabanı Yönetimi</h2>
          <p className="text-gray-600 mt-1">MongoDB: avrasya_sportif</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchStats} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
          <Button onClick={exportDatabase} className="bg-blue-600 hover:bg-blue-700" disabled={loading}>
            <Download className="w-4 h-4 mr-2" />
            Yedek Al
          </Button>
        </div>
      </div>

      {/* Database Stats */}
      {stats && (
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Database className="w-8 h-8" />
                <div>
                  <p className="text-sm opacity-90">Toplam Collection</p>
                  <p className="text-2xl font-bold">{stats.totalCollections || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8" />
                <div>
                  <p className="text-sm opacity-90">Toplam Kayıt</p>
                  <p className="text-2xl font-bold">{stats.totalDocuments || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Database className="w-8 h-8" />
                <div>
                  <p className="text-sm opacity-90">Veritabanı Boyutu</p>
                  <p className="text-2xl font-bold">{formatBytes(stats.dataSize || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-red-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8" />
                <div>
                  <p className="text-sm opacity-90">Storage Boyutu</p>
                  <p className="text-2xl font-bold">{formatBytes(stats.storageSize || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Collections List */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold">Collections</h3>
        
        <div className="grid gap-4">
          {collections.map((collection) => (
            <Card key={collection.name} className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Database className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-800">{collection.name}</h4>
                      <div className="flex gap-4 mt-1">
                        <Badge className="bg-blue-500">
                          {collection.count || 0} kayıt
                        </Badge>
                        <Badge variant="outline">
                          {formatBytes(collection.size || 0)}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => viewCollection(collection.name)}
                      disabled={loading || !collection.count}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Görüntüle
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => clearCollection(collection.name)}
                      disabled={loading || !collection.count}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Temizle
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {collections.length === 0 && !loading && (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              Henüz collection oluşturulmamış
            </CardContent>
          </Card>
        )}
      </div>

      {/* View Collection Data Modal */}
      <Dialog open={showDataModal} onOpenChange={setShowDataModal}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedCollection} - Veriler ({collectionData.length} kayıt)
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-2">
            {collectionData.map((doc, index) => (
              <Card key={index} className="bg-gray-50">
                <CardContent className="pt-4">
                  <pre className="text-xs overflow-x-auto">
                    {JSON.stringify(doc, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DatabaseTab;
